import { ReactElement, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useToggle } from 'react-use';

import { AddressInformation, BillingAddressData } from '@frontend/api';
import {
  AccordionButton,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Checkbox,
  CreatableSelect,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  IconButton,
  PencilBarIcon,
  Popover,
  PopoverAnchor,
  PopoverBody,
  PopoverButtonGroup,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  Tooltip,
} from '@frontend/components';

import { PAYMENT_METHOD } from 'constants/all';

import BillingAddressDrawer from 'modules/common/drawers/billingAddressDrawer/BillingAddressDrawer';
import { open as openBillingAddressDrawer } from 'modules/common/drawers/billingAddressDrawer/data/actions';
import { MODES as BILLING_ADDRESS_DRAWER_MODES } from 'modules/common/drawers/billingAddressDrawer/data/constants';
import { SuggestedAddressModal } from 'modules/common/modals/suggestedAddressModal';

import { useAppSelector } from 'store/hooks';

import { areSameAddresses } from 'utils/addressUtils';
import { formatAddressString } from 'utils/formatters';

import {
  selectNewlyAddedBillingAddress,
  setBillingAddress,
  setBillingInformation,
} from '../data/actions';
import { useConfirmAddressState } from '../data/hooks';
import {
  getAccount,
  getBillingAddress,
  getBillingAddresses,
  getBillingInformation,
  getContact,
  getPaymentMethod,
  getShippingInformation,
} from '../data/reducer';
import { convertBillingAddressToBillingInformation } from '../data/utils';
import { AddressDisplay, InlinedAddressDisplay } from './AddressDisplay';
import {
  OneTimeAddressForm,
  useOneTimeAddressForm,
} from './OneTimeAddressForm';

type BillingAddressOption = {
  value: number;
  label: ReactElement;
};

const getBillingAddressOption = (billingAddress: BillingAddressData) => {
  const addressInformation = {
    attention: null,
    businessName: null,
    address: billingAddress.address,
  };
  return {
    label: (
      <AddressDisplay
        addressInformation={addressInformation}
        color="feature.secondary"
      />
    ),
    value: billingAddress.id,
  };
};

const getBillingAddressValue = (billingAddress: BillingAddressData) => {
  const addressInformation = {
    attention: null,
    businessName: null,
    address: billingAddress.address,
  };
  return {
    label: (
      <InlinedAddressDisplay
        addressInformation={addressInformation}
        color="feature.secondary"
        spanProps={{ fontWeight: 'heavy' }}
      />
    ),
    value: billingAddress.id,
  };
};

export const BillTo = () => {
  const dispatch = useDispatch();
  const paymentMethod = useAppSelector(getPaymentMethod);
  const billingInformation = useAppSelector(getBillingInformation) ?? null;
  const billingAddresses = useAppSelector(getBillingAddresses);
  const contact = useAppSelector(getContact);
  const account = useAppSelector(getAccount);
  const billingAddress = useAppSelector(getBillingAddress);

  const billingAddressesById = useMemo(() => {
    return Object.fromEntries(
      billingAddresses.map((billingAddress) => [
        billingAddress.id,
        billingAddress,
      ]),
    );
  }, [billingAddresses]);

  const canSelectBillingAddress = Boolean(account);

  const oneTimeAddress = useMemo(() => {
    return billingInformation && !billingAddress ? billingInformation : null;
  }, [billingInformation, billingAddress]);

  const [useOneTimeAddress, toggleUseOneTimeAddress] = useToggle(
    Boolean(oneTimeAddress) || !canSelectBillingAddress,
  );

  const [showOneTimeAddressForm, toggleShowOneTimeAddressForm] = useToggle(
    useOneTimeAddress && !oneTimeAddress,
  );

  const billingAddressOptions = useMemo(
    () => billingAddresses.map(getBillingAddressOption),
    [billingAddresses],
  );

  const selectBillingAddressOption = useCallback(
    (option: BillingAddressOption | null) => {
      if (!contact || !account) {
        return;
      }
      const billingAddress = billingAddressesById[option?.value];
      dispatch(setBillingAddress({ billingAddress }));
      const billingInformation = billingAddress
        ? convertBillingAddressToBillingInformation(
            account,
            contact,
            billingAddress,
          )
        : null;
      dispatch(setBillingInformation(billingInformation));
    },
    [billingAddressesById, account, contact, dispatch],
  );

  const setOneTimeAddress = useCallback(
    (address: AddressInformation) => {
      dispatch(setBillingInformation(address));
      dispatch(setBillingAddress({ billingAddress: undefined }));
      toggleShowOneTimeAddressForm(false);
    },
    [dispatch, toggleShowOneTimeAddressForm],
  );

  const {
    confirmAddressState: {
      isSuggestedAddressModalOpen,
      isUnvalidatedAddressPopoverOpen,
      pendingAddress,
      suggestedAddresses,
    },
    clearPendingAddress,
    confirmAddress,
    openSuggestedAddressModal,
    openUnvalidatedAddressPopover,
  } = useConfirmAddressState({ onConfirmAddress: setOneTimeAddress });

  const oneTimeAddressFormikProps = useOneTimeAddressForm({
    initialAddress: oneTimeAddress,
    openSuggestedAddressModal,
    openUnvalidatedAddressPopover,
    setOneTimeAddress,
  });

  const shippingInformation = useAppSelector(getShippingInformation) ?? null;
  const isUsingShippingAddress =
    !!shippingInformation?.address &&
    !!billingInformation?.address &&
    areSameAddresses(shippingInformation.address, billingInformation.address);

  const toggleUseShippingAddress = useCallback(
    (useShippingAddress: boolean) => {
      if (!shippingInformation) {
        console.error(
          'unexpected toggle to use shipping address when shipping information is not provided',
        );
      } else {
        if (useShippingAddress) {
          dispatch(setBillingInformation(shippingInformation));
          toggleUseOneTimeAddress(true);
          toggleShowOneTimeAddressForm(false);
          if (billingAddress) {
            dispatch(setBillingAddress({ billingAddress: undefined }));
          }
        } else {
          dispatch(setBillingInformation(null));
          if (useOneTimeAddress) {
            toggleShowOneTimeAddressForm(true);
          }
        }
      }
    },
    [
      dispatch,
      toggleUseOneTimeAddress,
      toggleShowOneTimeAddressForm,
      billingAddress,
      shippingInformation,
      useOneTimeAddress,
    ],
  );

  const isCreditCardOrder = paymentMethod === PAYMENT_METHOD.CREDIT_CARD;

  return (
    <AccordionItem>
      {({ isExpanded }) => (
        <>
          <AccordionButton>
            <Flex gap={1} overflow="hidden">
              <Box flexShrink={0}>
                Bill To{isCreditCardOrder && ' (Required)'}
              </Box>
              {!isExpanded ? (
                <InlinedAddressDisplay
                  color="feature.secondary"
                  spanProps={{ color: 'feature.secondary' }}
                  addressInformation={
                    billingInformation
                      ? {
                          attention: billingInformation.attention,
                          businessName: billingInformation.businessName,
                          address: billingInformation.address,
                        }
                      : null
                  }
                />
              ) : null}
            </Flex>
          </AccordionButton>
          <AccordionPanel
            as={Flex}
            flexDirection="column"
            gap={1.5}
            align="stretch"
          >
            <Checkbox
              isChecked={isUsingShippingAddress}
              onChange={(event) =>
                toggleUseShippingAddress(event.target.checked)
              }
              isDisabled={!shippingInformation}
            >
              Use shipping address
            </Checkbox>
            {useOneTimeAddress ? (
              <>
                {showOneTimeAddressForm ? (
                  <>
                    <OneTimeAddressForm
                      formikProps={oneTimeAddressFormikProps}
                    />
                    <SuggestedAddressModal
                      isOpen={isSuggestedAddressModalOpen}
                      onClose={clearPendingAddress}
                      originalAddress={pendingAddress}
                      suggestedAddresses={suggestedAddresses}
                      onConfirmAddress={confirmAddress}
                    />
                  </>
                ) : (
                  <Flex gap={2}>
                    <Box>
                      <AddressDisplay addressInformation={oneTimeAddress} />
                    </Box>
                    <Tooltip placement="bottom" label="Edit one-time address">
                      <IconButton
                        onClick={() => toggleShowOneTimeAddressForm(true)}
                      >
                        <PencilBarIcon />
                      </IconButton>
                    </Tooltip>
                  </Flex>
                )}
                <Flex justifyContent="space-between">
                  <Tooltip
                    placement="right"
                    label="Contact must be associated with an account to select a billing address"
                    isDisabled={canSelectBillingAddress}
                  >
                    <Button
                      size="md"
                      variant="link"
                      onClick={() => {
                        dispatch(setBillingInformation(null));
                        toggleUseOneTimeAddress(false);
                      }}
                      isDisabled={!canSelectBillingAddress}
                    >
                      Select Billing Address
                    </Button>
                  </Tooltip>
                  {showOneTimeAddressForm && (
                    <Popover
                      isOpen={isUnvalidatedAddressPopoverOpen}
                      placement="top"
                    >
                      <PopoverAnchor>
                        <Button
                          size="md"
                          onClick={oneTimeAddressFormikProps.submitForm}
                          isDisabled={!oneTimeAddressFormikProps.isValid}
                          isLoading={oneTimeAddressFormikProps.isSubmitting}
                        >
                          Save
                        </Button>
                      </PopoverAnchor>
                      <PopoverContent>
                        <PopoverHeader>
                          We couldn&apos;t validate your address
                        </PopoverHeader>
                        <PopoverBody>
                          An invalid billing address can cause delivery delays.
                        </PopoverBody>
                        <PopoverFooter>
                          <PopoverButtonGroup>
                            <Button
                              variant="secondary"
                              onClick={clearPendingAddress}
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => confirmAddress(pendingAddress)}
                            >
                              Proceed anyway
                            </Button>
                          </PopoverButtonGroup>
                        </PopoverFooter>
                      </PopoverContent>
                    </Popover>
                  )}
                </Flex>
              </>
            ) : account ? (
              <>
                <FormControl isRequired={isCreditCardOrder}>
                  <FormLabel>Customer Billing Address</FormLabel>
                  <CreatableSelect
                    createLabelEntity="billing address"
                    createOptionPosition="first"
                    options={billingAddressOptions}
                    value={
                      billingAddress
                        ? getBillingAddressValue(billingAddress)
                        : null
                    }
                    isClearable={true}
                    onCreateOption={() =>
                      dispatch(
                        openBillingAddressDrawer.request({
                          accountId: account.id,
                          callBack: ({ billingAddress }) => {
                            dispatch(
                              selectNewlyAddedBillingAddress.success({
                                billingAddress,
                              }),
                            );
                            dispatch(
                              setBillingInformation(
                                convertBillingAddressToBillingInformation(
                                  account,
                                  contact,
                                  billingAddress,
                                ),
                              ),
                            );
                          },
                          mode: BILLING_ADDRESS_DRAWER_MODES.CREATE,
                        }),
                      )
                    }
                    onChange={selectBillingAddressOption}
                    isValidNewOption={() => true}
                    getOptionValue={(option: BillingAddressOption) => {
                      if (option.value && billingAddressesById[option.value]) {
                        const billingAddress =
                          billingAddressesById[option.value];
                        return formatAddressString(billingAddress.address);
                      }
                      return option.value;
                    }}
                  />
                </FormControl>
                <Divider />
                <Button
                  size="md"
                  variant="link"
                  alignSelf="start"
                  onClick={() => {
                    dispatch(setBillingInformation(null));
                    dispatch(setBillingAddress({ billingAddress: undefined }));
                    toggleUseOneTimeAddress(true);
                    toggleShowOneTimeAddressForm(true);
                  }}
                >
                  Use one-time address
                </Button>
                <BillingAddressDrawer />
              </>
            ) : null}
          </AccordionPanel>
        </>
      )}
    </AccordionItem>
  );
};
